import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useUserStore from '../store/userStore';
import { refreshAuth, hasSessionToken, clearAuth } from '../utils/api';
import LoadErrorCard from './LoadErrorCard';

// Only a server answer that the session itself is invalid may sign the user
// out: 401 (missing / invalid / expired session), or /auth/me's 404 "User not
// found" (session points at a deleted account). Transport failures (offline,
// timeout, DNS, aborted) and 5xx / outages are NOT — the stored session is
// kept and the user gets a recoverable Retry instead of the Login screen.
export function isConfirmedAuthInvalid(error) {
  if (!error) return false;
  if (error.message === 'No session token') return true;
  var status = error.response && error.response.status;
  if (status === 401) return true;
  if (status === 404) {
    var detail = error.response.data && error.response.data.detail;
    return detail === 'User not found';
  }
  return false;
}

/**
 * ProtectedRoute - Requires authentication AND completed onboarding
 * 
 * State machine:
 * - loading: Initial state, checking auth
 * - authenticated: User exists with completed onboarding
 * - needs-onboarding: User exists but onboarding incomplete
 * - unauthenticated: No valid session
 * 
 * Key principle: Derive auth state from user presence, not separate boolean
 */
// Status known WITHOUT any request: a hydrated user (persisted store) or no
// token at all. 'loading' is reserved for the one genuinely async case — a
// token exists but no user is hydrated yet (/auth/me recovery below).
function initialStatus(user) {
  if (user) return user.onboarding_complete ? 'authenticated' : 'needs-onboarding';
  return hasSessionToken() ? 'loading' : 'unauthenticated';
}

const ProtectedRoute = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser, setProfile, logout } = useUserStore();
  const [status, setStatus] = useState(() => initialStatus(user));
  // Bumped by Retry after a non-auth recovery failure.
  const [attempt, setAttempt] = useState(0);
  const [recoverError, setRecoverError] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      const tokenExists = hasSessionToken();
      
      console.log('[ProtectedRoute] Checking auth:', {
        tokenExists,
        userInStore: !!user,
        onboardingComplete: user?.onboarding_complete
      });

      // Case 1: User already in store with completed onboarding
      if (user && user.onboarding_complete) {
        console.log('[ProtectedRoute] ✅ User authenticated with completed onboarding');
        setStatus('authenticated');
        return;
      }

      // Case 2: User in store but onboarding incomplete
      if (user && !user.onboarding_complete) {
        console.log('[ProtectedRoute] ⚠️ User needs onboarding');
        setStatus('needs-onboarding');
        return;
      }

      // Case 3: No token at all
      if (!tokenExists) {
        console.log('[ProtectedRoute] ❌ No token - unauthenticated');
        setStatus('unauthenticated');
        return;
      }

      // Case 4: Token exists but no user in store - refresh from API
      console.log('[ProtectedRoute] 🔄 Token exists, refreshing auth...');
      try {
        const { user: fetchedUser, profile } = await refreshAuth();
        
        // Update store
        setUser(fetchedUser);
        if (profile) setProfile(profile);
        
        // Check onboarding status
        if (fetchedUser.onboarding_complete) {
          console.log('[ProtectedRoute] ✅ Auth refreshed - authenticated');
          setStatus('authenticated');
        } else {
          console.log('[ProtectedRoute] ⚠️ Auth refreshed - needs onboarding');
          setStatus('needs-onboarding');
        }
      } catch (error) {
        console.log('[ProtectedRoute] ❌ Auth refresh failed:', error.message);
        if (isConfirmedAuthInvalid(error)) {
          // The server confirmed the session is invalid: clear it and redirect.
          logout();
          setStatus('unauthenticated');
        } else {
          // Offline / timeout / 5xx: keep the stored session, offer Retry.
          setRecoverError(error);
          setStatus('error');
        }
      }
    };

    checkAuth();
  }, [user, setUser, setProfile, logout, attempt]);

  // Handle redirects based on status
  useEffect(() => {
    if (status === 'unauthenticated') {
      console.log('[ProtectedRoute] Redirecting to /login');
      navigate('/login', { replace: true, state: { from: location.pathname } });
    } else if (status === 'needs-onboarding') {
      console.log('[ProtectedRoute] Redirecting to /onboarding/setup');
      navigate('/onboarding/setup', { replace: true });
    }
  }, [status, navigate, location.pathname]);

  // Loading state
  if (status === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-accent border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-muted-foreground text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-background px-4" data-testid="protected-route-error">
        <LoadErrorCard
          error={recoverError}
          title="We couldn't reach Fitxess"
          testId="protected-route-load-error"
          onRetry={() => { setStatus('loading'); setAttempt((a) => a + 1); }}
        />
      </div>
    );
  }

  // Only render children when authenticated
  if (status !== 'authenticated') {
    return null;
  }

  return children;
};

export default ProtectedRoute;
